import { Card, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Share2, Trash2, Tag, Pencil, Heart, Calendar, Clock, CheckCircle, ExternalLink } from "lucide-react";
import { GeneratedContent } from "@/types/content";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { motion } from "framer-motion";
import { format } from "date-fns";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";

interface ContentCardProps {
  content: GeneratedContent;
  onDelete?: (id: string) => Promise<void>;
  onPost?: (id: string) => Promise<void>;
  onEdit?: (id: string) => void;
  onFavorite?: (id: string) => void;
  isDeleting?: boolean;
  isPosting?: boolean;
  isEditing?: boolean;
  isFavoriting?: boolean;
  onClick?: () => void;
  showActions?: boolean;
  isHighlighted?: boolean;
  id?: string;
}

// Platform color mapping
const platformColors: Record<string, { bg: string, text: string, border: string, darkBg: string, darkText: string, darkBorder: string }> = {
  blog: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    darkBg: 'dark:bg-blue-900/20',
    darkText: 'dark:text-blue-300',
    darkBorder: 'dark:border-blue-800/30',
  },
  twitter: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    darkBg: 'dark:bg-blue-900/20',
    darkText: 'dark:text-blue-300',
    darkBorder: 'dark:border-blue-800/30',
  },
  linkedin: {
    bg: 'bg-sky-50',
    text: 'text-sky-700',
    border: 'border-sky-200',
    darkBg: 'dark:bg-sky-900/20',
    darkText: 'dark:text-sky-300',
    darkBorder: 'dark:border-sky-800/30',
  },
  email: {
    bg: 'bg-emerald-50',
    text: 'text-emerald-700',
    border: 'border-emerald-200',
    darkBg: 'dark:bg-emerald-900/20',
    darkText: 'dark:text-emerald-300',
    darkBorder: 'dark:border-emerald-800/30',
  },
  social: {
    bg: 'bg-blue-50',
    text: 'text-blue-700',
    border: 'border-blue-200',
    darkBg: 'dark:bg-blue-900/20',
    darkText: 'dark:text-blue-300',
    darkBorder: 'dark:border-blue-800/30',
  },
  video: {
    bg: 'bg-red-50',
    text: 'text-red-700',
    border: 'border-red-200',
    darkBg: 'dark:bg-red-900/20',
    darkText: 'dark:text-red-300',
    darkBorder: 'dark:border-red-800/30',
  },
  podcast: {
    bg: 'bg-amber-50',
    text: 'text-amber-700',
    border: 'border-amber-200',
    darkBg: 'dark:bg-amber-900/20',
    darkText: 'dark:text-amber-300',
    darkBorder: 'dark:border-amber-800/30',
  },
  reddit: {
    bg: 'bg-orange-50',
    text: 'text-orange-700',
    border: 'border-orange-200',
    darkBg: 'dark:bg-orange-900/20',
    darkText: 'dark:text-orange-300',
    darkBorder: 'dark:border-orange-800/30',
  },
  instagram: {
    bg: 'bg-pink-50',
    text: 'text-pink-700',
    border: 'border-pink-200',
    darkBg: 'dark:bg-pink-900/20',
    darkText: 'dark:text-pink-300',
    darkBorder: 'dark:border-pink-800/30',
  },
  topics: {
    bg: 'bg-violet-50',
    text: 'text-violet-700',
    border: 'border-violet-200',
    darkBg: 'dark:bg-violet-900/20',
    darkText: 'dark:text-violet-300',
    darkBorder: 'dark:border-violet-800/30',
  },
  // Fallback for any other platform
  default: {
    bg: 'bg-zinc-50',
    text: 'text-zinc-700',
    border: 'border-zinc-200',
    darkBg: 'dark:bg-zinc-800/50',
    darkText: 'dark:text-zinc-300',
    darkBorder: 'dark:border-zinc-700',
  }
};

export const ContentCard = ({
  content,
  onDelete,
  onPost,
  onEdit,
  onFavorite,
  isDeleting,
  isPosting,
  isEditing,
  isFavoriting,
  onClick,
  showActions = false,
  isHighlighted = false,
  id
}: ContentCardProps) => {
  // Function to truncate text
  const truncateText = (text: string | null | undefined, maxLength: number) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Get platform colors
  const colors = platformColors[content.platform] || platformColors.default;

  // Format date if available
  const formattedDate = content.created_at 
    ? format(new Date(content.created_at), 'MMM d, yyyy')
    : 'Unknown date';

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      id={id}
    >
      <Card 
        className={cn(
          "relative group cursor-pointer transition-all duration-200",
          "hover:shadow-md hover:translate-y-[-2px]",
          "border border-zinc-200 dark:border-zinc-700",
          "bg-white dark:bg-zinc-800",
          isHighlighted && "ring-2 ring-blue-500 dark:ring-blue-400 shadow-lg animate-highlight"
        )}
        onClick={onClick}
      >
        {/* Status indicator */}
        {content.status === 'completed' && content.is_posted && (
          <div className="absolute top-0 right-0 w-0 h-0 border-t-[40px] border-r-[40px] border-t-transparent border-r-blue-500 dark:border-r-blue-600 z-10"></div>
        )}
        
        {/* New content indicator */}
        {isHighlighted && (
          <>
            <div className="absolute -top-1 -right-1 -left-1 -bottom-1 rounded-lg pointer-events-none z-0"></div>
            <div className="absolute top-4 right-4 flex items-center justify-center z-20">
              <div className="bg-blue-600 dark:bg-blue-500 text-white px-2 py-0.5 rounded text-xs font-bold shadow-md animate-pulse">
                NEW
              </div>
            </div>
          </>
        )}
        
        {/* Platform badge */}
        <div className="absolute top-4 left-4">
          <Badge className={cn(
            "px-2 py-1 text-xs font-medium rounded-md",
            colors.bg, colors.text, colors.border,
            colors.darkBg, colors.darkText, colors.darkBorder
          )}>
            {content.platform}
          </Badge>
          
          {/* In-depth badge if reviewed content is available */}
          {content.metadata?.reviewedContent && (
            <Badge className="ml-2 px-2 py-1 text-xs font-medium rounded-md bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-emerald-200 dark:border-emerald-800/30">
              In-depth
            </Badge>
          )}
        </div>

        {/* Action buttons */}
        {showActions && (
          <div className="absolute top-3 right-3 flex gap-1.5 z-10">
            <TooltipProvider>
              {onPost && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant={content.is_posted ? "default" : "outline"}
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPost(content.id);
                      }}
                      disabled={isPosting}
                      className={cn(
                        "h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity",
                        content.is_posted ? "bg-blue-600 hover:bg-blue-700" : ""
                      )}
                    >
                      {content.is_posted ? (
                        <CheckCircle className="h-4 w-4" />
                      ) : (
                        <ExternalLink className="h-4 w-4" />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{content.is_posted ? "Unmark as Posted" : "Mark as Posted"}</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {onEdit && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onEdit(content.id);
                      }}
                      disabled={isEditing}
                      className="h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Edit Content</p>
                  </TooltipContent>
                </Tooltip>
              )}
              
              {onDelete && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="outline"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDelete(content.id);
                      }}
                      disabled={isDeleting}
                      className="h-8 w-8 rounded-full opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-50 hover:text-red-600 hover:border-red-200 dark:hover:bg-red-900/20 dark:hover:text-red-400 dark:hover:border-red-800/30"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Delete Content</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </TooltipProvider>
          </div>
        )}

        <CardContent className="pt-12 pb-4">
          <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 line-clamp-2 mb-2">
            {content.title}
          </h3>
          <p className="text-zinc-600 dark:text-zinc-400 text-sm mt-2 line-clamp-3">
            {content.metadata?.reviewedContent ? 
              truncateText(content.metadata.reviewedContent, 150) : 
              truncateText(content.content || '', 150)}
          </p>
        </CardContent>
        
        <CardFooter className="flex items-center justify-between pt-0 pb-4 px-6">
          <div className="flex items-center gap-1 text-xs text-zinc-500 dark:text-zinc-400">
            <Clock className="h-3 w-3 mr-1" />
            {formattedDate}
          </div>
          
          {content.metadata?.audience && (
            <Badge variant="outline" className="text-xs">
              {content.metadata.audience}
            </Badge>
          )}
        </CardFooter>
      </Card>
    </motion.div>
  );
};
